import { useCallback, useEffect, useState } from 'react';
import { Download, Trash2 } from 'lucide-react';
import { projectsAPI, timeAPI } from '../lib/api';
import { getUser } from '../lib/auth';
import { fmtDate, inputCls, btnGhost } from '../components/ui';

function fmtHours(seconds) {
  return (seconds / 3600).toFixed(2);
}

export default function TimePage() {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ total_seconds: 0, billable_seconds: 0 });
  const [projects, setProjects] = useState([]);
  const [roster, setRoster] = useState([]);
  const [filters, setFilters] = useState({ project: '', member: '', start: '', end: '' });
  const user = getUser();
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await timeAPI.report(params);
    setEntries(data.entries);
    setTotals({ total_seconds: data.total_seconds, billable_seconds: data.billable_seconds });
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    projectsAPI.list().then(({ data }) => setProjects(data.projects));
    if (isAdmin) projectsAPI.roster().then(({ data }) => setRoster(data.members));
  }, [isAdmin]);

  const downloadCsv = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await timeAPI.csv(params);
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'time-report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="mr-2 text-lg font-semibold">Time</h1>
        <select value={filters.project} onChange={set('project')} className={`${inputCls} w-auto py-1.5 text-xs`}>
          <option value="">All projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        {isAdmin && (
          <select value={filters.member} onChange={set('member')} className={`${inputCls} w-auto py-1.5 text-xs`}>
            <option value="">All people</option>
            {roster.map((m) => (
              <option key={m.mid} value={m.mid}>
                {m.name}
              </option>
            ))}
          </select>
        )}
        <input type="date" value={filters.start} onChange={set('start')} className={`${inputCls} w-auto py-1.5 text-xs`} />
        <span className="text-xs text-pm-faint">to</span>
        <input type="date" value={filters.end} onChange={set('end')} className={`${inputCls} w-auto py-1.5 text-xs`} />
        <button type="button" onClick={downloadCsv} className={`${btnGhost} ml-auto inline-flex items-center gap-1.5 py-1.5 text-xs`}>
          <Download size={13} />
          CSV
        </button>
      </div>

      <div className="mb-4 flex gap-3">
        <div className="rounded-lg border border-pm-border bg-pm-surface px-4 py-3 shadow-[var(--pm-elev-1)]">
          <div className="text-xs text-pm-faint">Total</div>
          <div className="text-lg font-semibold"><span className="pm-num">{fmtHours(totals.total_seconds)}</span> h</div>
        </div>
        <div className="rounded-lg border border-pm-border bg-pm-surface px-4 py-3 shadow-[var(--pm-elev-1)]">
          <div className="text-xs text-pm-faint">Billable</div>
          <div className="text-lg font-semibold text-pm-accent"><span className="pm-num">{fmtHours(totals.billable_seconds)}</span> h</div>
        </div>
        <div className="rounded-lg border border-pm-border bg-pm-surface px-4 py-3 shadow-[var(--pm-elev-1)]">
          <div className="text-xs text-pm-faint">Entries</div>
          <div className="pm-num text-lg font-semibold">{entries.length}</div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-pm-border bg-pm-surface shadow-[var(--pm-elev-1)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-pm-border text-left text-xs text-pm-faint">
              <th className="px-3 py-2 font-medium">Date</th>
              {isAdmin && <th className="px-3 py-2 font-medium">Person</th>}
              <th className="px-3 py-2 font-medium">Project</th>
              <th className="px-3 py-2 font-medium">Task</th>
              <th className="px-3 py-2 font-medium">Note</th>
              <th className="px-3 py-2 text-center font-medium">Billable</th>
              <th className="px-3 py-2 text-right font-medium">Hours</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="group border-b border-pm-border/50">
                <td className="whitespace-nowrap px-3 py-2 text-xs text-pm-muted">{fmtDate(e.started_at)}</td>
                {isAdmin && <td className="px-3 py-2 text-xs">{e.member_name}</td>}
                <td className="px-3 py-2 text-xs">{e.project_name}</td>
                <td className="max-w-48 truncate px-3 py-2">{e.task_title}</td>
                <td className="max-w-32 truncate px-3 py-2 text-xs text-pm-muted">{e.note}</td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={Boolean(e.billable)}
                    onChange={async (ev) => {
                      await timeAPI.patchEntry(e.id, { billable: ev.target.checked });
                      await load();
                    }}
                    className="accent-[var(--pm-accent)]"
                  />
                </td>
                <td className="pm-num px-3 py-2 text-right text-xs">{fmtHours(e.seconds)}</td>
                <td className="px-1 py-2">
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm('Delete this entry?')) return;
                      await timeAPI.deleteEntry(e.id);
                      await load();
                    }}
                    className="rounded p-1 text-pm-faint opacity-0 hover:text-pm-danger group-hover:opacity-100"
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 8 : 7} className="px-3 py-10 text-center text-sm text-pm-muted">
                  No time entries — start a timer from any task panel.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
